import React from 'react';
import { BaseStep, useFormData, BaseStepProps } from '../BaseStep';
import FormSection from '../../../FormSection';
import ownershipIcon from '../../../../assets/icons/ownership.png';
import tractionIcon from '../../../../assets/icons/traction.png';
import starIcon from '../../../../assets/icons/star.png';
import AmountSlider from '../AmountSlider';
import { TextArea } from '../TextArea';
import {
  ownershipSelectOptions,
  tractionSelectOptions,
  expectedTractionSelectOptions,
} from '../../../../constants/ownership';
import { useProfile } from '../../../../contexts/ProfileContext';
import {
  IMPRESSIVE_FACTOR_MAX_CHARS,
  IMPRESSIVE_FACTOR_MIN_CHARS,
} from '../../../../constants/profileFieldLimits';

const OwnershipStep: React.FC<BaseStepProps> = ({
  userType,
  onCreateProfile,
  hasProfile,
  onComplete,
  shouldValidate,
  onValidationHandled,
  onValidationResult,
}) => {
  const { startupProfile } = useProfile();

  // Map traction values from old format to new format if needed
  const mapTractionValue = (tractionValue: string | undefined): string => {
    if (!tractionValue) return '';
    // Create defaults used 'no-users'; keep blank until ownership has been saved.
    if (tractionValue === 'no-users') {
      const lastCompleted = startupProfile?.lastCompletedStep ?? -1;
      return lastCompleted >= 3 ? 'no-users-clients-yet' : '';
    }
    if (tractionValue === 'users-no-revenue') return 'users-clients-no-revenue';
    return tractionValue;
  };

  const { formData, errors, handleInputChange, setFormData, validateForm, setErrors } = useFormData(
    {
      capitalization:
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (startupProfile as any)?.capitalization || '', // TODO: Add to StartupProfile type
      traction: mapTractionValue(startupProfile?.traction), // This field exists in StartupProfile
      annualRevenue: String(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (startupProfile as any)?.annualRevenue || 0
      ),
      revenueExpectations:
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (startupProfile as any)?.revenueExpectations || '', // TODO: Add to StartupProfile type
      impressiveFactor: (startupProfile?.impressiveFactor || '').slice(
        0,
        IMPRESSIVE_FACTOR_MAX_CHARS
      ),
    }
  );

  // Get currency from profile or default to EUR
  const currency = startupProfile?.currency || 'eur';

  // State for revenue slider multiplier
  const [isMultiplierActive, setIsMultiplierActive] = React.useState(false);

  // State to control when to show validation errors
  const [showErrors, setShowErrors] = React.useState(false);

  // Listen for validation requests from parent
  React.useEffect(() => {
    if (shouldValidate) {
      const requiredFields = ['capitalization', 'impressiveFactor'];
      let isValid = validateForm(requiredFields);
      const impressiveFactor = String(formData.impressiveFactor || '').trim();
      if (impressiveFactor.length > 0 && impressiveFactor.length < IMPRESSIVE_FACTOR_MIN_CHARS) {
        isValid = false;
        setErrors(prev => ({
          ...prev,
          impressiveFactor: `Minimum ${IMPRESSIVE_FACTOR_MIN_CHARS} characters`,
        }));
      }

      // Show errors after validation
      setShowErrors(true);

      // Notify parent about validation result
      if (onValidationResult) {
        onValidationResult(isValid);
      }

      // Notify parent that validation was handled
      if (onValidationHandled) {
        onValidationHandled();
      }

      // If validation passed, clear errors after a short delay
      if (isValid) {
        setTimeout(() => {
          setShowErrors(false);
        }, 100);
      }
    }
  }, [
    shouldValidate,
    validateForm,
    onValidationHandled,
    onValidationResult,
    formData.impressiveFactor,
    setErrors,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Expose form data to parent component
  React.useEffect(() => {
    if (onCreateProfile) {
      onCreateProfile(formData);
    }
  }, [formData, onCreateProfile]);

  return (
    <BaseStep
      userType={userType}
      onCreateProfile={onCreateProfile}
      hasProfile={hasProfile}
      onComplete={onComplete}
    >
      <FormSection icon={ownershipIcon} title="Ownership">
        <div>
          <label
            htmlFor="capitalization"
            className="block text-sm sm:text-base font-medium text-white mb-2 text-left"
          >
            Capitalization
          </label>
          <select
            id="capitalization"
            name="capitalization"
            value={String(formData.capitalization || '')}
            onChange={e => {
              // Keep local state first; parent sync via effect on the next paint.
              const value = e.target.value;
              setFormData(prev => ({ ...prev, capitalization: value }));
              if (errors.capitalization) {
                setErrors(prev => {
                  const next = { ...prev };
                  delete next.capitalization;
                  return next;
                });
              }
            }}
            className={`w-full px-2 py-1.5 sm:px-3 sm:py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-gray-900 ${
              showErrors && errors.capitalization ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            {ownershipSelectOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.name}
              </option>
            ))}
          </select>
          {showErrors && errors.capitalization && (
            <p className="text-red-500 text-sm mt-1 text-right">{errors.capitalization}</p>
          )}
        </div>
      </FormSection>

      <FormSection icon={tractionIcon} title="Traction">
        <div>
          <label
            htmlFor="traction"
            className="block text-sm sm:text-base font-medium text-white mb-2 text-left"
          >
            Traction
          </label>
          <select
            id="traction"
            name="traction"
            value={String(formData.traction || '')}
            onChange={handleInputChange}
            className={`w-full px-2 py-1.5 sm:px-3 sm:py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-gray-900 ${
              showErrors && errors.traction ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            {tractionSelectOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.name}
              </option>
            ))}
          </select>
          {showErrors && errors.traction && (
            <p className="text-red-500 text-sm mt-1 text-right">{errors.traction}</p>
          )}
        </div>

        {formData.traction === 'revenue-exists' && (
          <div>
            <label className="block text-sm sm:text-base font-medium text-white mb-2 text-left">
              Company&apos;s current annual revenue [optional]
            </label>
            <AmountSlider
              value={Number(formData.annualRevenue || '0')}
              onChange={val => setFormData(prev => ({ ...prev, annualRevenue: String(val) }))}
              currency={currency.toUpperCase()}
              multiplier={10}
              min={0}
              max={10000000}
              step={1000}
              isMultiplierActive={isMultiplierActive}
              onMultiplierToggle={() => setIsMultiplierActive(!isMultiplierActive)}
              showManualInput
              inputLabel={`Annual revenue ${currency.toUpperCase()}`}
            />
          </div>
        )}

        {formData.traction &&
          formData.traction !== '' &&
          formData.traction !== 'revenue-exists' && (
            <div>
              <label
                htmlFor="revenueExpectations"
                className="block text-sm sm:text-base font-medium text-white mb-2 text-left"
              >
                Revenue Expectations
              </label>
              <select
                id="revenueExpectations"
                name="revenueExpectations"
                value={String(formData.revenueExpectations || '')}
                onChange={handleInputChange}
                className={`w-full px-2 py-1.5 sm:px-3 sm:py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-gray-900 ${
                  showErrors && errors.revenueExpectations ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                {expectedTractionSelectOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.name}
                  </option>
                ))}
              </select>
              {showErrors && errors.revenueExpectations && (
                <p className="text-red-500 text-sm mt-1 text-right">{errors.revenueExpectations}</p>
              )}
            </div>
          )}
      </FormSection>

      <FormSection icon={starIcon} title="Impressive factor">
        <TextArea
          name="impressiveFactor"
          label="What might impress an investor the most about your company? *"
          required
          value={String(formData.impressiveFactor || '')}
          onChange={handleInputChange}
          placeholder="This is your chance to stand out from other startups in your industry."
          rows={8}
          maxCharacters={IMPRESSIVE_FACTOR_MAX_CHARS}
          minCharacters={IMPRESSIVE_FACTOR_MIN_CHARS}
          error={showErrors ? errors.impressiveFactor : undefined}
        />
      </FormSection>
    </BaseStep>
  );
};

export default OwnershipStep;
